import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  createColumnHelper,
  flexRender,
  getSortedRowModel,
} from '@tanstack/react-table';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '../../components/ui/table';
import { formatDate, formatFileSize, getFileType, getFilename } from './format';
import { useAuth0 } from '@auth0/auth0-react';
import { downloadFile } from '../../utils/downloadFile';
import { TablePagination } from '../table-common/tablePagination';
import { Loader2 } from 'lucide-react';
import { TextFilter } from '../table-common/filters/text-filter';
import { DateRange } from 'react-day-picker';
import { DateFilter } from '../table-common/filters/date-filter';

type FileInfo = {
  name: string;
  updated: string;
  size: number;
  content_type: string;
};

interface FileListProps {
  files: FileInfo[];
  isLoading: boolean;
  error: string;
  onRefresh: () => void;
}

const columnHelper = createColumnHelper<FileInfo>();

const FileList: React.FC<FileListProps> = ({
  files,
  isLoading,
  error,
  onRefresh,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [downloadingFile, setDownloadingFile] = React.useState<string | null>(
    null
  );
  const columns = [
    columnHelper.display({
      id: 'serial',
      header: 'Sl No.',
      cell: (info) => info.row.index + 1,
    }),
    columnHelper.accessor('name', {
      header: 'File Name',
      cell: (info) => (
        <div className="font-medium">{getFilename(info.getValue())}</div>
      ),
    }),
    columnHelper.accessor('updated', {
      id: 'updated',
      header: 'Uploaded Date',
      cell: (info) => formatDate(info.getValue()),
      filterFn: (row, columnId, filterValue: DateRange | undefined) => {
        if (!filterValue || !filterValue.from || !filterValue.to) return true;
        const rowDate = new Date(row.getValue(columnId));
        const from = new Date(filterValue.from);
        const to = new Date(filterValue.to);
        to.setHours(23, 59, 59, 999);
        return rowDate >= from && rowDate <= to;
      },
      enableSorting: true,
    }),
    columnHelper.accessor('size', {
      header: 'Size',
      cell: (info) => formatFileSize(info.getValue()),
    }),
    columnHelper.display({
      id: 'type',
      header: 'Type',
      cell: (info) => (
        <span className="px-2 py-1 text-xs bg-gray-100 rounded-full">
          {getFileType(info.row.original.name)}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'download',
      header: 'Download',
      cell: (info) => {
        const fileName = info.row.original.name;
        const handleDownload = async () => {
          setDownloadingFile(fileName);
          try {
            const authToken = await getAccessTokenSilently();
            await downloadFile(authToken, fileName);
          } finally {
            setDownloadingFile(null);
          }
        };
        return (
          <Button
            onClick={handleDownload}
            className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
            disabled={downloadingFile === fileName}
          >
            {downloadingFile === fileName ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            )}
            Download
          </Button>
        );
      },
    }),
  ];

  const table = useReactTable<FileInfo>({
    data: files,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [
        {
          id: 'updated',
          desc: true,
        },
      ],
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="text-gray-600">Loading files...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-red-600 text-center">
          <h3 className="text-lg font-semibold mb-2">Error Loading Files</h3>
          <p className="text-sm">{error}</p>
        </div>
        <Button onClick={onRefresh}>Retry</Button>
      </div>
    );
  }

  if (!files || files.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-gray-500 text-center">
          <h3 className="text-lg font-semibold mb-2">No Files Found</h3>
          <p className="text-sm">The storage bucket appears to be empty.</p>
        </div>
        <Button onClick={onRefresh}>Refresh</Button>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-2 p-8 pt-0 pb-0">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Files ({files.length})</h2>
        <Button
          onClick={onRefresh}
          disabled={isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Refresh
        </Button>
      </div>
      <div className="overflow-x-auto shadow-lg max-h-[calc(100vh-220px)] overflow-auto rounded-[10px] border">
        <Table>
          <TableHeader className="sticky top-0 bg-muted z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <React.Fragment key={headerGroup.id}>
                <TableRow className="!border-b-0 bg-muted">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
                <TableRow className="border-t-0 bg-muted">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="border-t-0">
                      {header.column.id === 'name' ? (
                        <TextFilter
                          column={header.column}
                          placeholder="Enter File Name"
                        />
                      ) : header.column.id === 'updated' ? (
                        <DateFilter column={header.column} />
                      ) : null}
                    </TableHead>
                  ))}
                </TableRow>
              </React.Fragment>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.map((row, index) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {cell.column.id === 'serial'
                      ? index + 1
                      : flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination table={table} />
    </div>
  );
};

export default FileList;
